import { ctxmenu, type CTXMenu } from "ctxmenu";
import { WEBSIM_TAG_ID_ATTRIBUTE } from "../hyperbase/html_transform/websim_tag_id_attribute";
import { parentApi } from "./message-proxy";

let menuElement: HTMLUListElement | null = null;
let isProjectOwner: boolean | null = null;

async function setIsProjectOwner() {
  const user = await window.websim?.getUser();
  const projectOwner = await window.websim?.getCreatedBy();
  isProjectOwner = projectOwner?.id === user?.id;
}

function handleContextMenu(event: MouseEvent) {
  if (!isProjectOwner) {
    return;
  }
  if (__WEBSIM_DATA__.isTopLevelFrame) {
    return;
  }
  if (event.defaultPrevented) {
    return;
  }

  event.stopImmediatePropagation();
  event.preventDefault();

  const target = event.target;

  if (!(target instanceof Element)) {
    return;
  }

  const activeTargets = new Set<Element>();
  activeTargets.add(target);

  const menu: CTXMenu = [];

  const selection = document.getSelection()?.toString();
  // don't show this if you are not the project owner
  if (isProjectOwner) {
    menu.push({
      text: "Prompt",
      action: () => {
        target.classList.remove("websim-focused");
        const closestTag = target.closest(`[${WEBSIM_TAG_ID_ATTRIBUTE}]`);
        if (!closestTag) {
          alert("No tag found");
          return;
        }
        const tagId = closestTag.getAttribute(WEBSIM_TAG_ID_ATTRIBUTE);
        if (!tagId) {
          alert("No tag id found");
          return;
        }
        const p = prompt("Edit this element");
        if (!p) {
          return;
        }
        const html = target.outerHTML;
        const truncatedHtml = html.slice(0, 1500);
        parentApi.navigate({
          source: "edit",
          prompt: {
            text: `User Selection: ${truncatedHtml}
          Prompt: ${p}`,
            type: "plaintext",
            data: null,
          },
        });
      },
    });
  }

  if (selection) {
    menu.push({
      text: "Copy",
      action: () => {
        navigator.clipboard.writeText(selection);
      },
    });
  } else {
    if (target !== document.body && target !== document.documentElement) {
      target.classList.add("websim-focused");
    }

    const link = target.closest("a[href]");

    if (link) {
      menu.push(
        {
          text: "Open Link in New Tab",
          action: () => {
            parentApi.navigate({
              method: "GET",
              url: link.getAttribute("href")!,
              target: "_blank",
              source: "link",
            });
          },
        },
        {
          text: "Copy Link Address",
          action: () => {
            navigator.clipboard.writeText(link.getAttribute("href")!);
          },
        }
      );
    }

    const image = target.closest("img");

    if (image) {
      menu.push(
        {
          text: "Copy Image",
          action: () => {
            const imageToCopy = new Image();
            const c = document.createElement("canvas");
            const ctx = c.getContext("2d");

            function urlToBlob(path: string, func: { (imgBlob: any): void }) {
              imageToCopy.onload = function () {
                c.width = (this as any).naturalWidth;
                c.height = (this as any).naturalHeight;
                (ctx as any).drawImage(this, 0, 0);
                c.toBlob((blob) => {
                  func(blob);
                }, "image/png");
              };
              imageToCopy.crossOrigin = "Anonymous";
              imageToCopy.src = path;
            }
            urlToBlob(image.src, (imgBlob) => {
              navigator.clipboard.write([
                new ClipboardItem({ "image/png": imgBlob }),
              ]);
            });
          },
        },
        {
          text: "Copy Image Address",
          action: () => {
            navigator.clipboard.writeText(image.src);
          },
        },
        {
          text: "Open Image in New Tab",
          action: () => {
            window.open(image.src, "_blank");
          },
        }
      );
    }
  }
  // don't show this if the url contains "LOGGED_IN"
  if (!window.location.href.includes("LOGGED_IN")) {
    menu.push({
      text: "View Source",
      action: () => {
        parentApi.openEditor();
      },
    });
  }

  menu.push({
    text: "Hide this menu",
    action: () => {
      window.removeEventListener("touchend", handleTouchEnd);
      window.removeEventListener("contextmenu", handleContextMenu);
      window.removeEventListener("touchstart", handleTouchStart);
    },
  });

  ctxmenu.show(menu, event, {
    onShow(element: HTMLUListElement) {
      menuElement = element;
    },
    onHide() {
      menuElement = null;
      target.classList.remove("websim-focused");
    },
  });
}

function handleTouchStart(event: TouchEvent) {
  if (menuElement?.contains(event.target as Node)) {
    return;
  }

  ctxmenu.hide();
}

function createDoubleTapHandler(fn: (event: TouchEvent) => void) {
  let lastTap = 0;
  return function (event: TouchEvent) {
    const currentTime = Date.now();
    const tapLength = currentTime - lastTap;
    if (tapLength < 500) {
      lastTap = 0;
      event.preventDefault();
      event.stopImmediatePropagation();
      fn(event);
    } else {
      lastTap = currentTime;
    }
  };
}

const handleTouchEnd = createDoubleTapHandler((event) => {
  event.target?.dispatchEvent(
    new MouseEvent("contextmenu", {
      bubbles: true,
      cancelable: false,
      button: 2,
      clientX: event.changedTouches[0].clientX,
      clientY: event.changedTouches[0].clientY,
    })
  );
});

export function initContextMenu() {
  setIsProjectOwner();

  window.addEventListener("DOMContentLoaded", () => {
    window.addEventListener("touchend", handleTouchEnd);
    window.addEventListener("contextmenu", handleContextMenu);
    window.addEventListener("touchstart", handleTouchStart);
  });
}
